import { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { MetricCard } from '@/components/ui/metric-card';
import { OnboardingTable } from '@/components/ui/onboarding-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import CodeEditor from '@/components/common/CodeEditor';
import { useFileContent } from '@/components/smartai/api/getFileContent';
import type { AstAnalysis } from '@/components/smartai/api/processFiles';
import { extractAllElements } from '@/utils/extractElements';
import OnboardBgWrapper from '@/components/smartai/backgroundImage/OnboardBgWrapper';
import OnboardingPageHeader from '@/components/smartai/ComponentOnboarding/OnboardingPageHeader';
import OnboardingProjectHeader from '@/components/smartai/ComponentOnboarding/OnboardingProjectHeader';
import { InfoAlert } from '@/components/smartai/ComponentOnboarding/onboarding-layout';
import { FileLevelBreakdown } from './FileLevelBreakdown';
import { cn } from '@/lib/utils';

export interface ASTNode {
  type: 'program' | 'class' | 'method' | 'function' | 'branch' | 'loop';
  name?: string;
  startLine?: number;
  endLine?: number;
  lineStart?: number;
  lineEnd?: number;
  executableLines: number[];
  nonExecutableLines: number[];
  children: ASTNode[];
}

export interface AST {
  type: 'program';
  children: ASTNode[];
}

export interface FileDetail {
  fileName: string;
  filePath: string;
  totalLines: number;
  totalExecutableLines: number;
  totalNonExecutableLines: number;
  totalClasses: number;
  totalMethods: number;
  totalFunctions: number;
  totalBranches: number;
  totalLoops: number;
  error: string | null;
  abstractSyntxTree: AST;
}

type FlatASTNode = Omit<ASTNode, 'children'>;

function getEntityBadgeClass(type: ASTNode['type']): string {
  const base =
    'inline-block rounded border border-solid px-2 py-0.5 text-xs font-semibold uppercase tracking-wide';
  switch (type) {
    case 'class':
      return cn(base, 'border-blue-200 bg-blue-50 text-blue-800');
    case 'method':
      return cn(base, 'border-green-200 bg-green-50 text-green-800');
    case 'function':
      return cn(
        base,
        'border-[var(--inv-accent)]/20 bg-[var(--inv-accent-soft)] text-[var(--inv-accent-hover)]',
      );
    case 'branch':
      return cn(base, 'border-pink-200 bg-pink-50 text-pink-800');
    case 'loop':
      return cn(base, 'border-purple-200 bg-purple-50 text-purple-800');
    default:
      return cn(
        base,
        'border-[var(--inv-border)] bg-[var(--inv-surface-muted)] text-[var(--inv-text-secondary)]',
      );
  }
}

function getEntityName(type: ASTNode['type']): string {
  switch (type) {
    case 'program':
      return 'Program';
    case 'class':
      return 'Class';
    case 'method':
      return 'Method';
    case 'function':
      return 'Function';
    case 'branch':
      return 'Branch';
    case 'loop':
      return 'Loop';
    default:
      return type;
  }
}

interface InventoryEntityDetailsPanelProps {
  fileName: string;
  entities: FlatASTNode[];
  onClose: () => void;
}

function InventoryEntityDetailsPanel({
  fileName,
  entities,
  onClose,
}: InventoryEntityDetailsPanelProps) {
  return (
    <div className="mt-6 border-t border-[var(--inv-border)] pt-4">
      <div className="mb-2 flex items-center justify-between gap-4">
        <h3 className="m-0 break-all font-serif text-lg font-semibold text-[var(--inv-text)]">
          Entity Details — <code className="font-mono text-sm">{fileName}</code>
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 border-0 p-0 shadow-none text-[var(--inv-text-secondary)] hover:bg-[var(--inv-surface-muted)] hover:text-[var(--inv-text)]"
          onClick={onClose}
          aria-label="Close entity details">
          <X className="h-4 w-4" />
        </Button>
      </div>
      {entities.length === 0 ? (
        <p className="py-2 text-sm text-[var(--inv-text-muted)]">
          No entity details available for this file.
        </p>
      ) : (
        <OnboardingTable
          data={entities}
          rowKey={row => `${row.type}-${row.name ?? ''}-${row.startLine ?? 0}`}
          columns={[
            {
              key: 'type',
              header: 'Type',
              render: (row: FlatASTNode) => (
                <span className={getEntityBadgeClass(row.type)}>
                  {getEntityName(row.type)}
                </span>
              ),
            },
            {
              key: 'name',
              header: 'Name',
              render: (row: FlatASTNode) => (
                <code className="font-mono text-sm">{row.name || '--No name--'}</code>
              ),
            },
            { key: 'startLine', header: 'Line Start', render: (row: FlatASTNode) => row.startLine },
            { key: 'endLine', header: 'Line End', render: (row: FlatASTNode) => row.endLine },
            {
              key: 'totalLines',
              header: 'Total Lines',
              render: (row: FlatASTNode) =>
                (row.endLine ?? 0) - (row.startLine ?? 0) + 1,
            },
          ]}
        />
      )}
    </div>
  );
}

interface ViewCodeModalProps {
  visible: boolean;
  file: FileDetail | null;
  repoUrl: string;
  branchName: string;
  scanId: string;
  pat: string;
  onClose: () => void;
}

function ViewCodeModal({
  visible,
  file,
  repoUrl,
  branchName,
  scanId,
  pat,
  onClose,
}: ViewCodeModalProps) {
  const filePath = file?.filePath ?? file?.fileName ?? '';

  const { data, isPending, isError } = useFileContent({
    repoUrl,
    branchName,
    filePath,
    scanId,
    pat,
  });

  const executableLines = data?.data?.lineClassification?.executable ?? [];

  return (
    <Dialog open={visible} onOpenChange={open => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] w-[92vw] max-w-[min(92vw,72rem)] flex-col gap-4 overflow-hidden p-6">
        <DialogHeader>
          <DialogTitle className="break-all pr-8">
            {file?.fileName ?? file?.filePath ?? 'View code'}
          </DialogTitle>
        </DialogHeader>
        {isPending ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : null}
        {isError ? (
          <p className="p-4 text-sm text-[#b42318]">Failed to load file content.</p>
        ) : null}
        {data?.data ? (
          <div className="min-h-0 flex-1 overflow-auto">
            <CodeEditor
              codeFromApi={data.data.content}
              language={data.data.language ?? filePath}
              executable={executableLines}
              nonExecutable={data.data.lineClassification?.nonExecutable ?? []}
              instanceId={filePath}
            />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function notStoredFiles(fileDetails: FileDetail[]) {
  return fileDetails.filter(file => Boolean(file.error));
}

function ScanPersistNotice({
  selectedCount,
  fileDetails,
}: {
  selectedCount: number;
  fileDetails: FileDetail[];
}) {
  const skipped = notStoredFiles(fileDetails);
  if (!skipped.length) return null;
  const storedCount = fileDetails.length - skipped.length;
  return (
    <InfoAlert className="mb-5">
      <p className="m-0 mb-2 text-sm">
        {selectedCount} files were selected for processing. {storedCount} were
        stored in inventory. {skipped.length} could not be stored because
        parsing failed:
      </p>
      <ul className="m-0 max-h-48 list-disc overflow-auto pl-5 text-sm">
        {skipped.map(file => (
          <li key={file.filePath || file.fileName} className="mb-1">
            <code className="font-mono text-[0.8rem]">
              {file.filePath || file.fileName}
            </code>
            {' — '}
            {file.error}
          </li>
        ))}
      </ul>
    </InfoAlert>
  );
}

function SummaryCards({
  report,
  selectedCount,
  storedCount,
}: {
  report: AstAnalysis;
  selectedCount: number;
  storedCount: number;
}) {
  const stats: [string, number | string][] = [
    ['Total Files', storedCount],
    ['Selected for processing', selectedCount],
    ['Lines of Code', report.totalLines],
    ['Boilerplate', report.totalNonExecutableLines ?? '-'],
    ['Executable', report.totalExecutableLines ?? '-'],
    ['Classes', report.totalClasses],
    ['Methods', report.totalMethods],
    ['Functions', report.totalFunctions],
    ['Branches', report.totalBranches ?? '-'],
    ['Loops', report.totalLoops ?? '-'],
  ];

  return (
    <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
      {stats.map(([label, value]) => (
        <MetricCard key={label} title={label} value={value} />
      ))}
    </div>
  );
}

export interface OnboardInventoryContentProps {
  org: string;
  azureProject: string;
  branchName: string;
  prEmails: string[];
  autoPrEmails: string[];
  mainJobId: string | undefined;
  testJobId: string | undefined;
  onCheckMainPR: () => void;
  onCheckMainBuild: () => void;
  onCheckAutoPR: () => void;
  onCheckAutoBuild: () => void;
  notification: { message: string; kind: 'success' | 'error' } | null;
  scanResult: { astAnalysis: AstAnalysis } | null;
  fileDetails: FileDetail[];
  selectedCount?: number;
  repoUrl: string;
  scanId: string;
  pat: string;
  repoName: string;
}

const OnboardInventoryContent = ({
  org,
  azureProject,
  branchName,
  mainJobId,
  testJobId,
  onCheckMainPR,
  onCheckMainBuild,
  onCheckAutoPR,
  onCheckAutoBuild,
  notification,
  scanResult,
  fileDetails,
  selectedCount = 0,
  repoUrl,
  scanId,
  pat,
  repoName,
}: OnboardInventoryContentProps) => {
  const [selectedFile, setSelectedFile] = useState<FileDetail | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [showEntityDetails, setShowEntityDetails] = useState(false);
  const [entityFile, setEntityFile] = useState<FileDetail | null>(null);
  const [entityElements, setEntityElements] = useState<FlatASTNode[]>([]);
  const entityPanelRef = useRef<HTMLDivElement>(null);

  const handleViewCode = (file: FileDetail) => {
    setSelectedFile(file);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedFile(null);
  };

  const handleViewEntityDetails = (file: FileDetail) => {
    const elements = extractAllElements(file.abstractSyntxTree);
    setEntityFile(file);
    setEntityElements(elements);
    setShowEntityDetails(true);
  };

  const handleCloseEntityDetails = () => {
    setShowEntityDetails(false);
    setEntityFile(null);
    setEntityElements([]);
  };

  useEffect(() => {
    if (showEntityDetails) {
      entityPanelRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [showEntityDetails, entityFile]);

  return (
    <OnboardBgWrapper>
      <div className="box-border min-h-[calc(100vh-3.5rem)] w-full max-w-[1400px] mx-auto px-7 pb-12 pt-8 max-[768px]:px-4 max-[768px]:pb-8 max-[768px]:pt-6">
        <OnboardingPageHeader
          eyebrow="Onboarding"
          title="Onboard inventory"
          subtitle="Review scan results, pipeline status, and file-level breakdown before finishing setup."
        />

        {notification ? (
          <div
            role="status"
            className={cn(
              'mb-4 rounded-[10px] border border-solid px-4 py-3 text-sm font-medium',
              notification.kind === 'success'
                ? 'border-[#abefc6] bg-[#ecfdf3] text-[#027a48]'
                : 'border-[#fecdca] bg-[#fef3f2] text-[#b42318]',
            )}>
            {notification.message}
          </div>
        ) : null}

        <OnboardingProjectHeader
          org={org}
          project={azureProject}
          repoName={repoName}
          branchName={branchName}
        />

        {mainJobId ? (
          <div className="mb-5 flex flex-wrap gap-8">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--inv-text-muted)]">
                Main Pipeline
              </span>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="secondary" onClick={onCheckMainPR}>
                  PR Status
                </Button>
                <Button type="button" variant="secondary" onClick={onCheckMainBuild}>
                  Build Status
                </Button>
              </div>
            </div>
            {testJobId ? (
              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--inv-text-muted)]">
                  Automation Pipeline
                </span>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="secondary" onClick={onCheckAutoPR}>
                    PR Status
                  </Button>
                  <Button type="button" variant="secondary" onClick={onCheckAutoBuild}>
                    Build Status
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {scanResult?.astAnalysis ? (
          <SummaryCards
            report={scanResult.astAnalysis}
            selectedCount={selectedCount || fileDetails.length}
            storedCount={
              fileDetails.length
                ? fileDetails.length - notStoredFiles(fileDetails).length
                : scanResult.astAnalysis.totalFiles
            }
          />
        ) : null}

        <ScanPersistNotice
          selectedCount={selectedCount || fileDetails.length}
          fileDetails={fileDetails}
        />

        {fileDetails.length > 0 ? (
          <>
            <h3 className="mb-3 mt-6 font-serif text-lg font-semibold text-[var(--inv-text)]">
              File-Level Breakdown
            </h3>
            <FileLevelBreakdown
              rows={fileDetails.map(row => ({
                id: row.filePath || row.fileName,
                path: row.filePath || row.fileName,
                metrics: [
                  { label: 'Lines', value: row.totalLines },
                  { label: 'Functions', value: row.totalFunctions },
                  { label: 'Classes', value: row.totalClasses },
                  { label: 'Methods', value: row.totalMethods },
                  { label: 'Branches', value: row.totalBranches },
                  { label: 'Loops', value: row.totalLoops },
                  { label: 'Exec. code', value: row.totalExecutableLines },
                  {
                    label: 'Non-exec.',
                    value: row.totalNonExecutableLines,
                  },
                ],
                onViewCode: () => handleViewCode(row),
                onViewEntities: () => handleViewEntityDetails(row),
              }))}
            />
          </>
        ) : null}

        {showEntityDetails && entityFile ? (
          <div ref={entityPanelRef}>
            <InventoryEntityDetailsPanel
              fileName={entityFile.fileName}
              entities={entityElements}
              onClose={handleCloseEntityDetails}
            />
          </div>
        ) : null}

        <ViewCodeModal
          visible={modalVisible}
          file={selectedFile}
          repoUrl={repoUrl}
          branchName={branchName}
          scanId={scanId}
          pat={pat}
          onClose={handleCloseModal}
        />
      </div>
    </OnboardBgWrapper>
  );
};

export default OnboardInventoryContent;
